<script setup lang="ts">
import type { INavLinkItem } from '~/components/navbar/NavigationLinks.vue'
import type { MegamenuNavItem } from '~/data/megamenu'
import { sendMenuClick } from '~/composables/useGtmEvents'

const props = defineProps<{
  navLinks: INavLinkItem[]
  megamenuData: MegamenuNavItem[]
}>()

const emit = defineEmits<{
  (e: 'navbarClose'): void
}>()

const hoveredIndex = ref<number | null>(null)
const hoverTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const closeTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const itemByPath = computed(() => {
  const map = new Map<string, MegamenuNavItem>()
  for (const item of props.megamenuData) {
    map.set(item.path, item)
  }
  return map
})

function getMegamenuItem(path: string): MegamenuNavItem | undefined {
  return itemByPath.value.get(path)
}

function hasDropdown(item: INavLinkItem) {
  const full = getMegamenuItem(item.path)
  return (full?.sections?.length ?? 0) > 0 || (full?.items?.length ?? 0) > 0
}

function onNavEnter(index: number) {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
  hoverTimeout.value = setTimeout(() => {
    hoveredIndex.value = index
    hoverTimeout.value = null
  }, 100)
}

function onNavLeave() {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value)
    hoverTimeout.value = null
  }
  closeTimeout.value = setTimeout(() => {
    hoveredIndex.value = null
    closeTimeout.value = null
  }, 150)
}

function onDropdownEnter() {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
}

function onDropdownLeave() {
  onNavLeave()
}

const hoveredItem = computed(() => {
  if (hoveredIndex.value == null) return null
  const link = props.navLinks[hoveredIndex.value]
  if (!link) return null
  return getMegamenuItem(link.path) ?? null
})

function setHoverOrigin(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  if (el.dataset.originLocked === 'true') return
  const rect = el.getBoundingClientRect()
  const x = e.clientX - rect.left
  const third = rect.width / 3
  if (x < third) el.style.setProperty('--hover-origin', 'left')
  else if (x > rect.width - third)
    el.style.setProperty('--hover-origin', 'right')
  else el.style.setProperty('--hover-origin', 'center')
  el.dataset.originLocked = 'true'
}

function resetHoverOrigin(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  setTimeout(() => {
    el.dataset.originLocked = 'false'
    el.style.removeProperty('--hover-origin')
  }, 300)
}

function handleNavClick(item: INavLinkItem) {
  sendMenuClick({
    menuName: item.text,
    itemLabel: item.text,
    itemPath: item.path,
  })
  emit('navbarClose')
}
</script>

<template>
  <div
    data-test-id="megamenu-desktop"
    @mouseenter="onDropdownEnter"
    @mouseleave="onDropdownLeave"
  >
    <ul
      data-test-id="megamenu-desktop-nav-list"
      class="flex flex-col md:flex-row font-medium mt-0 space-y-4 md:space-y-0 md:space-x-8 rtl:space-x-reverse text-sm"
    >
      <li
        v-for="(item, index) in navLinks"
        :key="item.path"
        :data-test-id="`megamenu-desktop-nav-item-${index}`"
        class="border-b pb-4 md:border-none md:pb-0 relative"
      >
        <NuxtLinkLocale
          v-if="!hasDropdown(item)"
          :data-test-id="`megamenu-desktop-nav-link-${index}`"
          class="text-stone-800 text-sm font-normal navigation-link block"
          :to="item.path"
          @click="handleNavClick(item)"
        >
          {{ item.text }}
        </NuxtLinkLocale>
        <NuxtLinkLocale
          v-else
          :data-test-id="`megamenu-desktop-nav-link-${index}`"
          class="text-stone-800 text-sm font-normal navigation-link block"
          :class="{ 'navigation-link--active': hoveredIndex === index }"
          :to="item.path"
          @click="handleNavClick(item)"
          @mouseenter="
            (e: MouseEvent) => {
              setHoverOrigin(e)
              onNavEnter(index)
            }
          "
          @mouseleave="resetHoverOrigin"
        >
          {{ item.text }}
        </NuxtLinkLocale>
      </li>
    </ul>
    <Transition name="megamenu-dropdown">
      <MegamenuDropdown
        v-if="hoveredItem"
        :item="hoveredItem"
        :active="hoveredIndex != null"
      />
    </Transition>
  </div>
</template>

<style lang="postcss" scoped>
:deep(.megamenu-dropdown-enter-active),
:deep(.megamenu-dropdown-leave-active) {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}
:deep(.megamenu-dropdown-enter-from),
:deep(.megamenu-dropdown-leave-to) {
  opacity: 0;
  transform: translateY(-8px);
}
:deep(.megamenu-dropdown-leave-to) {
  transform: translateY(-4px);
}
@media (prefers-reduced-motion: reduce) {
  :deep(.megamenu-dropdown-enter-active),
  :deep(.megamenu-dropdown-leave-active) {
    transition-duration: 0.01ms;
  }
}

.navigation-link {
  @apply relative inline-block no-underline;
}
.navigation-link::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: currentColor;
  transform: scaleX(0);
  transform-origin: var(--hover-origin, center);
  transition:
    transform 0.3s ease,
    transform-origin 0.1s ease;
}
.navigation-link.router-link-active::after,
.navigation-link.router-link-exact-active::after,
.navigation-link--active::after,
.navigation-link:hover::after {
  transform: scaleX(1);
}
</style>
